import React, { useMemo } from "react";

import { faCopy, faEye, faHistory } from "@fortawesome/pro-regular-svg-icons";

import { IconButton } from "src/design-system/Icon";
import { useModal } from "src/design-system/Modal";
import { Tooltip } from "src/design-system/Tooltip";
import { BackfillQueryModal } from "src/featureCatalogue/Queries/BackfillQueryModal";
import { ManageQueryModal } from "src/featureCatalogue/Queries/ManageQueryModal";
import { getQueryEditPermissions, Query } from "src/featureCatalogue/utils";
import { useCapabilities } from "src/hooks/useCapabilities";

type TableActionsProps = {
  query: Query | undefined;
  canBackfill: boolean;
  canDuplicate: boolean;
  canView: boolean;
  showViewQuery?: boolean;
};

export const TableActions: React.FC<TableActionsProps> = ({
  query,
  canBackfill,
  canDuplicate,
  canView,
  showViewQuery = false,
}) => {
  const { featureQueriesCatalogue } = useCapabilities();
  const editPermissions = getQueryEditPermissions(
    query,
    featureQueriesCatalogue,
  );
  const {
    isOpen: isEditOpen,
    openModal: openEdit,
    closeModal: closeEdit,
    afterLeave: afterEditLeave,
  } = useModal();

  const {
    isOpen: isDuplicateOpen,
    openModal: openDuplicate,
    closeModal: closeDuplicate,
    afterLeave: afterDuplicateLeave,
  } = useModal();

  const {
    isOpen: isBackfillOpen,
    openModal: openBackfill,
    closeModal: closeBackfill,
    afterLeave: afterBackfillLeave,
  } = useModal();

  const duplicateQuery = useMemo(() => {
    if (!query) return undefined;
    const { id: _dropId, ...rest } = query;
    return {
      ...rest,
      columns: {
        ...query.columns,
        features: [
          {
            column_name: "",
            feature_key: "",
            feature_type: undefined,
            feature_default: "None",
            evaled_feature_default: null,
          },
        ],
      },
    };
  }, [query]);

  const options = [
    {
      icon: faHistory,
      action: openBackfill,
      title: "Backfill",
      canAccess: !showViewQuery && canBackfill,
    },
    {
      icon: faCopy,
      action: openDuplicate,
      title: "Duplicate",
      canAccess: !showViewQuery && canDuplicate,
    },
    {
      icon: faEye,
      action: openEdit,
      title: "View Query",
      canAccess: canView,
    },
  ];

  return (
    <>
      <div
        className="flex cursor-pointer items-center gap-x-3 opacity-0 transition-opacity group-hover:opacity-100"
        onClick={(e) => e.stopPropagation()}
      >
        {options
          .filter((option) => option.canAccess !== false)
          .map((option) => (
            <Tooltip key={option.title} title={option.title} asChild>
              <IconButton
                icon={option.icon}
                size="xs"
                onClick={option.action}
              />
            </Tooltip>
          ))}
      </div>

      <ManageQueryModal
        afterLeave={afterEditLeave}
        open={isEditOpen}
        query={query}
        readOnly={editPermissions.readOnly}
        readOnlyReason={editPermissions.readOnlyReason}
        onClose={closeEdit}
      />

      <ManageQueryModal
        afterLeave={afterDuplicateLeave}
        open={isDuplicateOpen}
        query={duplicateQuery}
        onClose={closeDuplicate}
      />

      <BackfillQueryModal
        afterLeave={afterBackfillLeave}
        open={isBackfillOpen}
        query={query}
        onClose={closeBackfill}
      />
    </>
  );
};
